import { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DashboardConfigSchema } from '@trinity/chat/types/dashboard-config';
import { ThirdPartyProviderDataSchema } from '@trinity/management-app-client';
import { DashboardBuilderProvider } from '@trinity/chat-components/containers/Dashboard/DashboardBuilderProvider';
import { DashboardDataContainer } from '@trinity/chat-components/containers/Dashboard/DashboardDataContainer';
import { ConfigAwareDataProvider } from '@trinity/chat-components/containers/Dashboard/DataProvider';
import { BuilderCanvas } from '../Builder/BuilderCanvas';
import { TableauEmbedding } from '../../TableauEmbedding/TableauEmbedding';
import { createDefaultRegistry } from '../registry';
import { usePage } from '../../../hooks/usePage';

// ─── Styled ───────────────────────────────────────────────────────────────────

const Root = styled(Box)(({ theme }) => ({
  width: '85%',
  maxWidth: '85%',
  height: 'calc(100% - 24px)',
  margin: '0 auto',
  marginTop: '24px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 5,
  border: `2px solid ${theme.palette.gray.light2}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const ScrollArea = styled(Box)({
  flex: 1,
  overflow: 'auto',
  minHeight: 0,
});

const IframeRoot = styled(Box)({
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
});

const Centered = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: 200,
});

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  /** The ID of the page to render, as returned by chat-api /pages/:id. */
  pageId: number;
  /**
   * Optional function used to proxy agent data-source requests through the
   * host app's backend. Required for widgets with dataSource.type="agent".
   * If not provided, agent widgets will render empty.
   */
  proxyFetch?: (data: { agentId: number; extensionPath: string }) => Promise<unknown>;
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * PageRenderer — renders a Page (by ID) in view-only mode inside the chat app.
 *
 * Page types:
 *  - `dashboard` / `custom_dashboard`: renders the DashboardConfig using the
 *    BuilderCanvas in read-only mode (no toolbar, sidebar, or drag handles).
 *  - `third_party`: renders an external embed based on `providerData.provider`:
 *    - `figma`   — plain iframe with Figma-appropriate padding/height
 *    - `dmi`     — iframe with absolute-position cropping to hide the external navbar
 *    - `tableau` — Tableau JS API embed (`<tableau-viz>`)
 */
export function PageRenderer({ pageId, proxyFetch }: Props): React.ReactElement {
  const { data: page, isLoading, isError } = usePage(pageId);

  const registry = useMemo(() => createDefaultRegistry(), []);
  const dataProvider = useMemo(
    () => new ConfigAwareDataProvider(proxyFetch ?? (() => Promise.resolve(null))),
    [proxyFetch],
  );

  const initialConfig = useMemo(() => {
    if (page?.type !== 'dashboard' && page?.type !== 'custom_dashboard') return undefined;
    const rawConfig = page.currentVersion?.config;
    if (!rawConfig) return undefined;
    const result = DashboardConfigSchema.safeParse(rawConfig);
    return result.success ? result.data : undefined;
  }, [page]);

  if (isLoading) {
    return (
      <Centered>
        <CircularProgress size={32} />
      </Centered>
    );
  }

  if (isError || !page) {
    return (
      <Centered>
        <Typography variant="body2" color="text.secondary">
          Unable to load page.
        </Typography>
      </Centered>
    );
  }

  if (page.type === 'third_party') {
    const providerResult = ThirdPartyProviderDataSchema.safeParse(page.providerData);
    if (!providerResult.success) {
      return (
        <Centered>
          <Typography variant="body2" color="text.secondary">
            No URL configured for this page.
          </Typography>
        </Centered>
      );
    }

    const provider = providerResult.data;

    if (provider.provider === 'figma') {
      return (
        <IframeRoot>
          <iframe
            src={provider.url}
            title={page.name}
            allowFullScreen
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
          />
        </IframeRoot>
      );
    }

    if (provider.provider === 'dmi') {
      const leftNavWidth = 62;
      const topNavHeightTotal = 208; // 60px top nav + 148px header
      const { topPadding, url } = provider;
      return (
        <IframeRoot
          style={{
            position: 'relative',
            width: `calc(100% + ${leftNavWidth}px)`,
            overflow: 'hidden',
            paddingTop: `${topPadding}px`,
          }}
        >
          <iframe
            src={url}
            title={page.name}
            style={{
              position: 'absolute',
              top: `calc(-${topNavHeightTotal}px + ${topPadding}px)`,
              left: `-${leftNavWidth}px`,
              width: '100%',
              height: `calc(100% + ${topNavHeightTotal}px - ${topPadding}px)`,
              border: 'none',
              boxSizing: 'border-box',
            }}
          />
        </IframeRoot>
      );
    }

    // tableau
    return (
      <IframeRoot>
        <TableauEmbedding displayUrl={provider.url} />
      </IframeRoot>
    );
  }

  // dashboard + custom_dashboard both use DashboardConfig rendering
  return (
    <Root>
      <ScrollArea>
        <DashboardBuilderProvider initialConfig={initialConfig} registry={registry}>
          <DashboardDataContainer provider={dataProvider} mode="live">
            <BuilderCanvas readOnly title={page.name} />
          </DashboardDataContainer>
        </DashboardBuilderProvider>
      </ScrollArea>
    </Root>
  );
}
